import { NavLink, Outlet } from 'react-router-dom'
import { CodemaxBrand } from './Brand'
import { PartnerMenu } from './PartnerMenu'
import { cx } from './ui/primitives'

/**
 * Top navigation, not a sidebar.
 *
 * The staff application's rail carries fifteen destinations in four sections and earns
 * its 240px. A partner has six, and a rail holding six items spends a quarter of the
 * viewport looking like an internal admin tool - which is the one thing this is not.
 *
 * There is no country switcher either. Country scopes almost everything in QuoteHero
 * because staff hold grants across several; a partner is one company with one home
 * country, and where their deals span countries the totals already separate by currency.
 * There is nothing to switch.
 */
const NAV = [
  { to: '/', label: 'Home', end: true },
  // Second, because it is the only destination used before a deal exists. Everything after
  // it is the record of one that already does.
  { to: '/rate-card', label: 'Rate Card', end: false },
  { to: '/deals', label: 'Deals', end: false },
  { to: '/statements', label: 'Statements', end: false },
  { to: '/payouts', label: 'Payouts', end: false },
  { to: '/company', label: 'Company', end: false },
] as const

export function PortalShell() {
  return (
    <div className="flex min-h-full flex-col">
      <header className="border-b border-line bg-surface">
        {/* Three columns from `md` up - lockup, navigation, person - so the navigation is
            centred on the header rather than on whatever space the other two leave over.
            Centring it inside a flex row would move it every time somebody's name got
            longer.

            Below `md` the same three children wrap: the lockup and the person share the
            first row, and the navigation takes a full-width second row, still centred. A
            grid that silently overlapped at 380px would be worse than one that admits it
            needs two rows. */}
        <div className="mx-auto w-full max-w-6xl px-5 py-3">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 md:grid md:grid-cols-[1fr_auto_1fr]">
            <div className="order-1 flex items-center gap-2.5 md:justify-self-start">
              <CodemaxBrand className="h-6" />

              {/* A label, not a second mark. The Codemax mark is the mark; this says which
                  Codemax application a partner is in, in the display face at body weight so
                  it reads as a caption and never competes with the logo beside it. */}
              <span aria-hidden className="hidden h-5 w-px bg-line sm:block" />
              <span className="hidden font-display text-[13px] font-medium text-ink-2 sm:block">
                Reseller Portal
              </span>
            </div>

            <div className="order-2 ml-auto flex items-center md:order-3 md:ml-0 md:justify-self-end">
              <PartnerMenu />
            </div>

            {/* Scrolls rather than wraps on a narrow phone: six destinations on one line
                is the readable form, and a nav that reflows to two ragged lines is not. */}
            <nav className="order-3 -mb-3 flex w-full justify-center gap-1 overflow-x-auto md:order-2 md:w-auto md:justify-self-center">
              {NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    cx(
                      'shrink-0 border-b-2 px-3 py-2 text-sm font-medium transition-colors',
                      isActive
                        ? 'border-accent text-ink'
                        : 'border-transparent text-muted hover:text-ink-2',
                    )
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>
          </div>
        </div>
      </header>

      {/* A centred column, unlike the staff application's full-bleed layout. Six columns
          of a partner's own figures on a 27-inch screen do not need the whole screen,
          and a line of text that wide is harder to read, not easier. */}
      <main className="mx-auto w-full max-w-6xl flex-1 px-5 py-7">
        <Outlet />
      </main>

      <footer className="border-t border-line-soft px-5 py-5">
        <p className="mx-auto w-full max-w-6xl text-[12px] text-faint">
          Codemax will never ask for your bank details by email, and nobody at Codemax can
          change them on your behalf without confirming it with you by telephone first.
        </p>
      </footer>
    </div>
  )
}
